import { useAppDispatch } from '@/hooks';
import { login } from '@/store';
import { useVerifyNumberMutation, useVerifyTelegramMutation } from '@/store/api/mfaApi';
import { showToast } from '@/utils';
import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import Modal, { ModalButton } from '@/components/common/Modal';

type OTPModalProps = {
  open: boolean;
  onClose: () => void;
  data: { username: string; type: string };
};

const OTPModal = ({ open, onClose, data }: OTPModalProps) => {
  const [verifyNumber, { isLoading: numberLoading }] = useVerifyNumberMutation();
  const [verifyTelegram, { isLoading: telegramLoading }] = useVerifyTelegramMutation();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const [code, setCode] = useState(Array(6).fill(''));
  const inputs = useRef<HTMLInputElement[]>([]);

  const handleChange = (value: string, index: number) => {
    if (!/^[0-9]?$/.test(value)) return;

    const newCode = [...code];
    newCode[index] = value;
    setCode(newCode);

    if (value && index < 5) {
      inputs.current[index + 1]?.focus();
    }
  };

  const handleBackspace = (e: React.KeyboardEvent<HTMLInputElement>, index: number) => {
    if (e.key === 'Backspace' && !code[index] && index > 0) {
      inputs.current[index - 1]?.focus();
    }
  };

  const handleSubmit = async () => {
    const otp = code.join('');

    if (otp.length !== 6) {
      showToast('Please enter complete OTP', 'error');
      return;
    }

    try {
      let result;

      if (data.type === 'telegram') {
        result = await verifyTelegram({
          username: data.username,
          token: otp,
        }).unwrap();
        dispatch(
          login({
            type: result.type,
            accessToken: result.accessToken,
          })
        );
        navigate('/home');
      } else {
        result = await verifyNumber({
          username: data.username,
          token: otp,
        }).unwrap();
        dispatch(
          login({
            type: result.type,
            accessToken: result.accessToken,
          })
        );
      }
      navigate('/home');

      console.log('Verification Success:', result);
      onClose();
    } catch (error: any) {
      console.log('Verification Failed:', error);
      showToast(error?.data?.message || 'Verification failed', 'error');
    }
  };

  const loading = numberLoading || telegramLoading;

  const cancel = () => {
    setCode(Array(6).fill(''));
    onClose();
  };

  return (
    <Modal
      open={open}
      onClose={cancel}
      title="Security Code Verification"
      description={
        data.type === 'telegram'
          ? 'Enter the 6-digit code sent to your Telegram'
          : 'Enter the 6-digit code from your authenticator'
      }
      size="sm"
      closeOnBackdrop={false}
      footer={
        <>
          <ModalButton variant="secondary" onClick={cancel}>
            Cancel
          </ModalButton>
          <ModalButton onClick={handleSubmit} disabled={loading}>
            {loading ? 'Verifying...' : 'Verify'}
          </ModalButton>
        </>
      }
    >
      <div className="flex justify-center gap-2 sm:gap-3">
        {code.map((digit, index) => (
          <input
            key={index}
            ref={(el) => {
              if (el) inputs.current[index] = el;
            }}
            type="text"
            inputMode="numeric"
            maxLength={1}
            value={digit}
            onChange={(e) => handleChange(e.target.value, index)}
            onKeyDown={(e) => handleBackspace(e, index)}
            className="size-11 rounded-lg border border-shell-border bg-shell-raised text-center text-lg font-semibold text-shell-fg outline-none transition focus:border-gold sm:size-12"
          />
        ))}
      </div>
    </Modal>
  );
};

export default OTPModal;
